// SCR-STAFF-962 (AP-ENG-01؛ US-ENG-020، 022؛ FLOW-15): صف مشاور — معوق، امروز و آینده با نوع، موضوع، هدف، آخرین تعامل و اقدام سریع (تماس، پیام، انجام با
// نتیجه، تبدیل یا رزرو، مشاوره و برآورد)؛ «+ سرنخ» با Alt+L؛ نمای مدیر: صف همه مشاوران و بار کاری هر نفر (eng.followup.view_all).
import AddIcon from '@mui/icons-material/Add';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import CheckIcon from '@mui/icons-material/Check';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { unwrap, type EngAssigneeOption, type EngFollowUpView } from '@phoenix/api-client';
import { useAccess, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, JalaliDate, LoadingState, PageHeader, StatusChip } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useSchedulingActions } from '../scheduling/SchedulingActions';
import { AssigneePicker, consultationPath, engKeys, leadPath, NEW_CONSULTATION_PATH, priorityTone } from './common';
import { CompleteFollowUpDialog, MessageDialog } from './FollowUpDialogs';
import { LeadDialog } from './LeadDialog';
import { useNewLeadShortcut } from './LeadsPage';

export const SCR_STAFF_962 = 'SCR-STAFF-962';

type Section = 'overdue' | 'today' | 'upcoming';

export function QueuePage() {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const navigate = useNavigate();
  const access = useAccess();
  const scheduling = useSchedulingActions();
  const leadWrite = useWriteAccess('eng.lead.manage');
  const write = useWriteAccess('eng.followup.manage');
  const manager = access.can('eng.followup.view_all');
  const [all, setAll] = useState(false);
  const [assignee, setAssignee] = useState<EngAssigneeOption | null>(null);
  const [completing, setCompleting] = useState<EngFollowUpView | null>(null);
  const [messaging, setMessaging] = useState<EngFollowUpView | null>(null);
  const [creatingLead, setCreatingLead] = useState(false);
  const [sent, setSent] = useState(false);
  useNewLeadShortcut(() => setCreatingLead(true), leadWrite.canWrite);

  const queue = useQuery({
    queryKey: [...engKeys.queue(session?.tenantId), all, assignee?.membershipId],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/engagement/followups/queue', {
          params: { query: { all: manager && all ? true : undefined, assigneeMembershipId: manager && !all ? assignee?.membershipId : undefined } },
        }),
      ),
    refetchInterval: 60_000,
  });

  const data = queue.data;

  const quickActions = (row: EngFollowUpView) => {
    const contact = row.contact;
    const canCall = Boolean(row.subjectMobile) && contact?.canCall !== false;
    return (
      <Stack direction="row" spacing={0.5}>
        <Tooltip title={canCall ? t('eng:queue.call') : t(`eng:contact_reason.${contact?.reason ?? 'NotAllowed'}`)}>
          <span>
            <IconButton
              size="small"
              component="a"
              href={canCall ? `tel:${row.subjectMobile}` : undefined}
              disabled={!canCall}
              aria-label={t('eng:queue.call')}
              data-testid={`queue-call-${row.id}`}
            >
              <CallOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={contact?.canMessage ? t('eng:queue.message') : t(`eng:contact_reason.${contact?.reason ?? 'NotAllowed'}`)}>
          <span>
            <IconButton size="small" onClick={() => setMessaging(row)} disabled={!write.canWrite || !contact?.canMessage} aria-label={t('eng:queue.message')} data-testid={`queue-message-${row.id}`}>
              <SmsOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('eng:queue.done')}>
          <span>
            <IconButton size="small" color="success" onClick={() => setCompleting(row)} disabled={!write.canWrite} aria-label={t('eng:queue.done')} data-testid={`queue-done-${row.id}`}>
              <CheckIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {row.subjectKind === 'Lead' && row.leadId && (
          <Tooltip title={t('eng:queue.convert')}>
            <IconButton size="small" onClick={() => navigate(leadPath(row.leadId!))} aria-label={t('eng:queue.convert')} data-testid={`queue-convert-${row.id}`}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {row.subjectKind === 'Patient' && row.patientId && scheduling?.canBook && (
          <Tooltip title={t('eng:queue.book')}>
            <IconButton
              size="small"
              onClick={() => scheduling.openBooking({ patient: { id: row.patientId!, displayName: row.subjectName ?? '' } })}
              aria-label={t('eng:queue.book')}
              data-testid={`queue-book-${row.id}`}
            >
              <EventAvailableOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {(row.quoteId || row.consultationId || access.can('eng.consultation.manage')) && (
          <Tooltip title={row.quoteId ? t('eng:queue.open_quote') : t('eng:queue.consultation')}>
            <IconButton
              size="small"
              onClick={() =>
                navigate(
                  row.quoteId
                    ? `/quotes/${row.quoteId}`
                    : row.consultationId
                      ? consultationPath(row.consultationId)
                      : `${NEW_CONSULTATION_PATH}?${row.subjectKind === 'Lead' ? `leadId=${row.leadId}` : `patientId=${row.patientId}`}`,
                )
              }
              aria-label={row.quoteId ? t('eng:queue.open_quote') : t('eng:queue.consultation')}
              data-testid={`queue-quote-${row.id}`}
            >
              <RequestQuoteOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    );
  };

  const renderSection = (section: Section, rows: readonly EngFollowUpView[]) => (
    <Paper variant="outlined" sx={{ p: 1.5 }} component="section" aria-label={t(`eng:queue.${section}`)} data-testid={`queue-${section}`}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        {section === 'overdue' && <WarningAmberOutlinedIcon color="warning" fontSize="small" />}
        <Typography variant="h3">
          {t(`eng:queue.${section}`)} · {faDigits(rows.length)}
        </Typography>
      </Stack>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('eng:queue.empty')}
        </Typography>
      ) : (
        <Table size="small" aria-label={t(`eng:queue.${section}`)}>
          <TableHead>
            <TableRow>
              <TableCell>{t('eng:queue.due')}</TableCell>
              <TableCell>{t('eng:queue.subject')}</TableCell>
              <TableCell>{t('eng:queue.goal')}</TableCell>
              <TableCell>{t('eng:queue.last')}</TableCell>
              {all && <TableCell>{t('eng:queue.assignee')}</TableCell>}
              <TableCell>{t('eng:queue.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} data-testid={`queue-row-${row.id}`}>
                <TableCell>
                  <JalaliDate value={row.dueAt} withTime />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                    <Button
                      size="small"
                      component={RouterLink}
                      to={row.subjectKind === 'Lead' && row.leadId ? leadPath(row.leadId) : `/patients/${row.patientId}`}
                      sx={{ p: 0, minWidth: 0 }}
                    >
                      {row.subjectName ?? '—'}
                    </Button>
                    <Chip size="small" variant="outlined" label={t(`eng:subject_kind.${row.subjectKind}`)} />
                    <StatusChip tone={priorityTone(row.priority)} labelKey={`eng:priority.${row.priority}`} />
                  </Stack>
                </TableCell>
                <TableCell>
                  {t(`eng:followup_type.${row.type}`)} · {row.goal}
                </TableCell>
                <TableCell>
                  {row.lastInteraction ? (
                    <Typography variant="caption">
                      {t(`eng:last_kind.${row.lastInteraction.kind}`)}
                      {row.lastInteraction.outcome ? ` · ${t(`eng:followup_outcome.${row.lastInteraction.outcome}`, { defaultValue: row.lastInteraction.outcome })}` : ''} ·{' '}
                      <JalaliDate value={row.lastInteraction.at} />
                    </Typography>
                  ) : (
                    '—'
                  )}
                </TableCell>
                {all && <TableCell>{row.assigneeName ?? '—'}</TableCell>}
                <TableCell>{quickActions(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );

  return (
    <section data-screen-id={SCR_STAFF_962}>
      <PageHeader
        titleKey={all ? 'eng:queue.title_all' : 'eng:queue.title'}
        titleValues={{ today: faDigits(data?.today.length ?? 0), overdue: faDigits(data?.overdue.length ?? 0) }}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreatingLead(true)} disabled={!leadWrite.canWrite} aria-keyshortcuts="Alt+L" data-testid="queue-new-lead">
            {t('eng:queue.new_lead')}
          </Button>
        }
      />
      <Stack spacing={2}>
        {manager && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <FormControlLabel control={<Switch checked={all} onChange={(event) => setAll(event.target.checked)} data-testid="queue-all" />} label={t('eng:queue.all_advisors')} />
            {!all && <AssigneePicker value={assignee} onChange={setAssignee} labelKey="eng:queue.advisor" testId="queue-advisor" />}
          </Stack>
        )}
        {sent && (
          <Alert severity="success" onClose={() => setSent(false)} data-testid="queue-message-sent">
            {t('eng:message_form.sent')}
          </Alert>
        )}
        {queue.isPending ? (
          <LoadingState rows={4} />
        ) : queue.error ? (
          <ErrorState error={queue.error} onRetry={() => void queue.refetch()} />
        ) : (
          data && (
            <>
              {renderSection('overdue', data.overdue)}
              {renderSection('today', data.today)}
              {renderSection('upcoming', data.upcoming)}
              {data.workload && (
                <Paper variant="outlined" sx={{ p: 1.5 }} component="section" aria-label={t('eng:queue.workload')} data-testid="queue-workload">
                  <Typography variant="h3" sx={{ mb: 1 }}>
                    {t('eng:queue.workload')}
                  </Typography>
                  <Table size="small" aria-label={t('eng:queue.workload')}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('eng:queue.advisor')}</TableCell>
                        <TableCell>{t('eng:queue.today')}</TableCell>
                        <TableCell>{t('eng:queue.overdue')}</TableCell>
                        <TableCell>{t('eng:queue.upcoming')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.workload.map((row) => (
                        <TableRow key={row.assigneeMembershipId}>
                          <TableCell>{row.name || '—'}</TableCell>
                          <TableCell>{faDigits(Number(row.today))}</TableCell>
                          <TableCell>{faDigits(Number(row.overdue))}</TableCell>
                          <TableCell>{faDigits(Number(row.upcoming))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </>
          )
        )}
      </Stack>
      {completing && <CompleteFollowUpDialog activity={completing} onClose={() => setCompleting(null)} />}
      {messaging && (
        <MessageDialog
          activity={messaging}
          onClose={(done) => {
            setMessaging(null);
            setSent(Boolean(done));
          }}
        />
      )}
      {creatingLead && (
        <LeadDialog
          onClose={(saved) => {
            setCreatingLead(false);
            if (saved) {
              navigate(leadPath(saved.id), { state: { merged: saved.merged } });
            }
          }}
        />
      )}
    </section>
  );
}
